import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Check, MessageCircle } from "lucide-react";
import confetti from "canvas-confetti";
import { Button } from "./ui/button";

const packages = [
  {
    name: "PAKET A",
    betina: "Rp 2.000.000",
    jantan: "Rp 2.500.000",
    sate: "200 Tusuk",
    gule: "40 Bungkus",
    berat: "12–13 kg",
  },
  {
    name: "PAKET B",
    betina: "Rp 2.300.000",
    jantan: "Rp 2.800.000",
    sate: "250 Tusuk",
    gule: "50 Bungkus",
    berat: "14–15 kg",
  },
  {
    name: "PAKET C",
    betina: "Rp 2.600.000",
    jantan: "Rp 3.100.000",
    sate: "300 Tusuk",
    gule: "60 Bungkus",
    berat: "15–16 kg",
  },
  {
    name: "PAKET D",
    betina: "Rp 2.900.000",
    jantan: "Rp 3.400.000",
    sate: "350 Tusuk",
    gule: "70 Bungkus",
    berat: "17–18 kg",
  },
  {
    name: "SUPER",
    betina: "Rp 3.200.000",
    jantan: "Rp 3.700.000",
    sate: "400 Tusuk",
    gule: "80 Bungkus",
    berat: "19–20 kg",
  },
  {
    name: "SUPER+",
    betina: "Rp 3.500.000",
    jantan: "Rp 4.100.000",
    sate: "450 Tusuk",
    gule: "90 Bungkus",
    berat: "20–21 kg",
  },
  {
    name: "ISTIMEWA",
    betina: "Rp 3.900.000",
    jantan: "Rp 4.400.000",
    sate: "500 Tusuk",
    gule: "100 Bungkus",
    berat: "22–23 kg",
  },
  {
    name: "KOTAKAN",
    special: true,
    harga: "Rp 3.650.000",
    porsi: "80 Kotak Nasi Lengkap",
    berat: "±17 kg",
  },
];

const fasilitasDefault = [
  "Sertifikat + Bingkai",
  "Dokumentasi Foto",
  "Video Zoom Meeting Pemotongan",
  "Gratis Ongkir (Jarak < 5km)",
];

const PricingPopup = () => {
  const [selected, setSelected] = useState(null);

  const handleWhatsAppClick = (packageName) => {
    const message = encodeURIComponent(
      `Halo Admin Toyibah Aqiqah 👋, saya tertarik dengan ${packageName}. Mohon informasi detailnya.`
    );
    window.open(`https://wa.me/6287884243038?text=${message}`, "_blank");
  };

  const handleKotakanClick = (pkg) => {
    confetti({
      particleCount: 150,
      spread: 70,
      origin: { y: 0.6 },
      colors: ["#58B09C", "#FF8A65", "#FFD8C8"],
    });
    setSelected(pkg);
  };

  return (
    <section
      id="pricing"
      className="relative py-20 px-6 bg-gradient-to-b from-[#FFFBF9] via-[#FFF7F4] to-[#FFFFFF] overflow-hidden"
    >
      {/* 🌸 Background Pattern */}
      <div
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='24' height='24' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M12 1L12 23M1 12L23 12' stroke='%23FF8A65' stroke-width='0.4'/%3E%3C/svg%3E")`,
          backgroundRepeat: "repeat",
        }}
      />

      {/* 🔶 Header */}
      <div className="relative z-10 text-center max-w-5xl mx-auto mb-10">
        <h2 className="text-3xl md:text-5xl font-extrabold text-[#333] mb-6">
          Pilih <span className="text-[#FF8A65]">Paket Aqiqah</span>
        </h2>
        <p className="text-[#777] text-base md:text-lg leading-relaxed">
          Klik salah satu paket di bawah ini untuk melihat detail harga, berat,
          dan menu pilihan Toyibah Aqiqah.
        </p>
      </div>

      {/* 🟧 Tombol Paket */}
      <div className="relative z-10 flex flex-wrap justify-center gap-4">
        {packages.map((pkg, i) => (
          <motion.button
            key={i}
            onClick={() =>
              pkg.special ? handleKotakanClick(pkg) : setSelected(pkg)
            }
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.97 }}
            className={`px-6 py-4 text-lg font-semibold rounded-xl shadow-md hover:shadow-lg transition-all ${
              pkg.special
                ? "bg-[#58B09C] text-white hover:bg-[#4AA18B]"
                : "bg-gradient-to-br from-[#FF8A65] to-[#FF6B4A] text-white"
            }`}
          >
            {pkg.name}
          </motion.button>
        ))}
      </div>

      {/* Popup Detail Paket */}
      <AnimatePresence>
        {selected && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex justify-center items-center px-4"
          >
            <motion.div
              initial={{ scale: 0.9, y: 30 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 30 }}
              transition={{ duration: 0.3 }}
              className="relative bg-white rounded-3xl shadow-2xl p-8 w-full max-w-lg border border-[#FFD8C8]"
            >
              {/* Tombol Close */}
              <button
                onClick={() => setSelected(null)}
                className="absolute top-3 right-3 text-[#FF8A65] hover:text-[#FF6B4A]"
              >
                <X className="w-6 h-6" />
              </button>

              {/* 🟢 Paket Kotakan */}
              {selected.special ? (
  <>
    {/* 🖼️ Gambar Kotakan */}
    <div className="w-full mb-5">
      <img
        src="/kotakan1.png"
        alt="Kotakan Aqiqah Toyibah"
        className="rounded-2xl w-full object-cover shadow-md border border-[#EAF7F4]
                   max-h-[240px] md:max-h-[320px]"
      />
    </div>

    {/* Judul & Deskripsi */}
    <h3 className="text-2xl md:text-3xl font-bold text-[#58B09C] mb-2 text-center">
      {selected.name}
    </h3>
    <p className="text-[#777] mb-5 text-center">
      Paket nasi kotakan lengkap dari <strong>Toyibah Aqiqah</strong>, siap disajikan
      dengan cita rasa istimewa & layanan syar’i.
    </p>

    {/* Harga & Porsi */}
    <div className="bg-[#EAF7F4] p-5 rounded-2xl mb-5 text-center">
      <p className="font-semibold text-[#58B09C] text-xl mb-1">
        {selected.harga}
      </p>
      <p className="text-[#555] text-sm">{selected.porsi}</p>
      <p className="text-[#777] text-sm italic">
        Berat kambing {selected.berat}
      </p>
    </div>

    {/* Fasilitas */}
    <p className="text-sm text-[#333] font-bold mb-2 text-center">
      Termasuk fasilitas:
    </p>
    <ul className="text-sm text-[#555] space-y-2 mb-6">
      {fasilitasDefault.map((f, i) => (
        <li key={i} className="flex items-start gap-2">
          <Check className="w-4 h-4 text-[#25D366] mt-[2px]" />
          {f}
        </li>
      ))}
    </ul>

    {/* Pesan Syariah */}
    <div className="bg-[#58B09C]/10 border border-[#58B09C]/30 p-4 rounded-xl text-[#58B09C] font-semibold text-sm mb-6 text-center">
      🕌 <b>Toyibah Aqiqah</b> menyembelihkan <b>1 ekor kambing untuk 1 anak</b> bukan untuk patungan ramai-ramai. Toyibah Aqiqah menjaga Syariat.
    </div>

    {/* Tombol WhatsApp */}
    <Button
      onClick={() => handleWhatsAppClick(selected.name)}
      className="w-full bg-[#58B09C] hover:bg-[#4AA18B] text-white py-4 rounded-xl font-semibold text-lg flex items-center justify-center gap-2 shadow-md hover:shadow-lg transition-all"
    >
      <MessageCircle className="w-5 h-5" /> Pesan via WhatsApp
    </Button>
  </>
) : (
                <>
                  {/* 🔶 Paket Standar */}
                  <h3 className="text-2xl font-bold text-[#333] mb-2">
                    {selected.name}
                  </h3>
                  <p className="text-[#777] mb-5">
                    Pilihan paket lengkap dan berkah dari{" "}
                    <strong>Toyibah Aqiqah</strong>.
                  </p>

                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="bg-[#FFF5F2] p-4 rounded-xl border border-[#FFD8C8]">
                      <p className="font-semibold text-[#FF8A65] text-sm">
                        Kambing Betina
                      </p>
                      <p className="text-lg font-bold text-[#333]">
                        {selected.betina}
                      </p>
                    </div>
                    <div className="bg-[#FFF5F2] p-4 rounded-xl border border-[#FFD8C8]">
                      <p className="font-semibold text-[#FF8A65] text-sm">
                        Kambing Jantan
                      </p>
                      <p className="text-lg font-bold text-[#333]">
                        {selected.jantan}
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-3 text-sm mb-3">
                    <span className="bg-[#FF8A65]/10 px-4 py-2 rounded-lg text-[#FF8A65] font-semibold">
                      Berat: {selected.berat}
                    </span>
                    <span className="bg-[#FF8A65]/10 px-4 py-2 rounded-lg text-[#FF8A65] font-semibold">
                      Sate: {selected.sate}
                    </span>
                    <span className="bg-[#FF8A65]/10 px-4 py-2 rounded-lg text-[#FF8A65] font-semibold">
                      Gule / Semur: {selected.gule}
                    </span>
                  </div>

                  <p className="text-sm font-bold text-[#333] mb-2">
                    Sudah termasuk fasilitas:
                  </p>
                  <ul className="text-sm text-[#555] space-y-2 mb-6">
                    {fasilitasDefault.map((f, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-[#25D366] mt-[2px]" />
                        {f}
                      </li>
                    ))}
                  </ul>

                  <Button
                    onClick={() => handleWhatsAppClick(selected.name)}
                    className="w-full bg-[#25D366] hover:bg-[#128C7E] text-white py-4 rounded-xl font-semibold text-lg flex items-center justify-center gap-2 shadow-md hover:shadow-lg transition-all"
                  >
                    <MessageCircle className="w-5 h-5" /> Pesan via WhatsApp
                  </Button>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default PricingPopup;
